import {ListViewSectionFilterLink} from '@github-ui/list-view/ListViewSectionFilterLink'
import {useMatch, useParams} from '@github-ui/react-router'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {useIsPlatform} from '@github-ui/use-is-platform'
import {useNavigate, useSearchParams} from '@github-ui/use-navigate'
import {clsx} from 'clsx'
import {useCallback, useMemo} from 'react'

import styles from './RepositoryProjects.module.css'

interface OpenClosedProjectsProps {
  openCount: number
  closedCount: number
}

export function OpenClosedProjects({openCount, closedCount}: OpenClosedProjectsProps) {
  const [searchParams] = useSearchParams()
  const stateParam = searchParams.get('state')
  const isMac = useIsPlatform(['mac'])
  const navigate = useNavigate()

  // Derive the base path from React Router params/match. This keeps SSR'd hrefs
  // correct (the no-JS link target matches the canonical route) and avoids the
  // React Compiler staleness bug from #20749/cd256af.
  const {owner = '', repo = ''} = useParams<{owner: string; repo: string}>()
  const isTemplatesView = useMatch('/:owner/:repo/projects/templates') !== null
  const basePath = useMemo(
    () => `/${owner}/${repo}/projects${isTemplatesView ? '/templates' : ''}`,
    [owner, repo, isTemplatesView],
  )

  const onClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>, isOpen: boolean) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      const isMetaKey = isMac ? event.metaKey : event.ctrlKey
      if (isMetaKey) {
        return
      }

      event.preventDefault()
      const params = new URLSearchParams(searchParams)
      if (isOpen) {
        params.delete('state')
      } else {
        params.set('state', 'closed')
      }
      const paramString = params.toString()
      const href = paramString ? `${basePath}?${paramString}` : basePath
      navigate(href, {preventAutofocus: true})
    },
    [basePath, isMac, navigate, searchParams],
  )

  const openHrefUrl = basePath
  const closedHrefUrl = `${basePath}?state=closed`

  const {isBrowser} = useRuntimeEnvironment()

  return (
    <div>
      <ul className={`list-style-none ${clsx(styles.tabsContainer)}`} aria-label="Filter projects by state">
        <li key="section-filter-0">
          <ListViewSectionFilterLink
            key="open"
            title="Open"
            isSelected={isBrowser && stateParam !== 'closed'}
            count={openCount.toLocaleString()}
            href={openHrefUrl}
            onClick={e => onClick(e, true)}
            data-testid="open-projects-tab"
          />
        </li>
        <li key="section-filter-1">
          <ListViewSectionFilterLink
            key="closed"
            title="Closed"
            isSelected={isBrowser && stateParam === 'closed'}
            count={closedCount.toLocaleString()}
            href={closedHrefUrl}
            onClick={e => onClick(e, false)}
            data-testid="closed-projects-tab"
          />
        </li>
      </ul>
    </div>
  )
}
